<script setup>
import { ref, computed, watch, onMounted } from "vue";
import {
  getCatalogoMaquinas,
  crearCatalogoMaquina,
  actualizarCatalogoMaquina,
  eliminarCatalogoMaquina,
} from "@/utils/api_backend";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";
import { usePaginacionMaquinasMoldsidStore } from "@/store/paginacionStore";
import CrudToolbar from "@/components/Shared/CrudToolbar.vue";
import ColumnFilterMenu from "@/components/Shared/ColumnFilterMenu.vue";

const paginacionStore = usePaginacionMaquinasMoldsidStore();

const registros = ref([]);
const filtro = ref("");
const loading = ref(false);
const guardando = ref(false);
const editando = ref(false);
const paginaActual = ref(1);
const modalElement = ref(null);
let modalInstance = null;

const form = ref({ id: null, nombre: "", descripcion: "" });

// ── Ordenamiento ───────────────────────────────────────────
const sortKey = ref("nombre");
const sortDir = ref("asc");

function onSort({ field, asc }) {
  sortKey.value = field;
  sortDir.value = asc ? "asc" : "desc";
  paginaActual.value = 1;
}

watch(filtro, () => {
  paginaActual.value = 1;
});

// ── Computed ───────────────────────────────────────────────
const filtrados = computed(() => {
  const s = filtro.value.toLowerCase();
  return registros.value.filter(
    (r) =>
      r.nombre.toLowerCase().includes(s) ||
      (r.descripcion || "").toLowerCase().includes(s)
  );
});

const ordenados = computed(() =>
  [...filtrados.value].sort((a, b) => {
    const cmp = String(a[sortKey.value] ?? "").localeCompare(
      String(b[sortKey.value] ?? ""),
      "es",
      { sensitivity: "base" }
    );
    return sortDir.value === "asc" ? cmp : -cmp;
  })
);

const porPagina = computed(() => paginacionStore.porPagina);
const totalPaginas = computed(() =>
  Math.max(1, Math.ceil(ordenados.value.length / porPagina.value))
);
const paginados = computed(() => {
  const inicio = (paginaActual.value - 1) * porPagina.value;
  return ordenados.value.slice(inicio, inicio + porPagina.value);
});
const rangoInicio = computed(() =>
  ordenados.value.length === 0 ? 0 : (paginaActual.value - 1) * porPagina.value + 1
);
const rangoFin = computed(() =>
  Math.min(paginaActual.value * porPagina.value, ordenados.value.length)
);
const paginasVisibles = computed(() => {
  const total = totalPaginas.value;
  const actual = paginaActual.value;
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set([1, total, actual]);
  if (actual > 1) set.add(actual - 1);
  if (actual < total) set.add(actual + 1);
  const ordenadas = [...set].sort((a, b) => a - b);
  const resultado = [];
  for (let i = 0; i < ordenadas.length; i++) {
    if (i > 0 && ordenadas[i] - ordenadas[i - 1] > 1) resultado.push("...");
    resultado.push(ordenadas[i]);
  }
  return resultado;
});

function cambiarPorPagina(n) {
  paginacionStore.setPorPagina(n);
  paginaActual.value = 1;
}

// ── CRUD ───────────────────────────────────────────────────
async function cargar() {
  loading.value = true;
  try {
    registros.value = await getCatalogoMaquinas();
  } catch {
    Swal.fire("Error", "No se pudieron cargar las máquinas", "error");
  } finally {
    loading.value = false;
  }
}

function abrirModal(item = null) {
  if (item) {
    editando.value = true;
    form.value = {
      id: item.id,
      nombre: item.nombre,
      descripcion: item.descripcion || "",
    };
  } else {
    editando.value = false;
    form.value = { id: null, nombre: "", descripcion: "" };
  }
  modalInstance.show();
}

async function guardar() {
  guardando.value = true;
  try {
    const payload = {
      nombre: form.value.nombre,
      descripcion: form.value.descripcion || null,
    };
    if (editando.value) {
      await actualizarCatalogoMaquina(form.value.id, payload);
    } else {
      await crearCatalogoMaquina(payload);
    }
    await cargar();
    modalInstance.hide();
    Swal.fire({
      icon: "success",
      title: "Operación exitosa",
      text: `Máquina ${editando.value ? "actualizada" : "registrada"} correctamente`,
      timer: 2000,
      showConfirmButton: false,
    });
  } catch {
    Swal.fire("Error", "No se pudo guardar la máquina", "error");
  } finally {
    guardando.value = false;
  }
}

async function confirmarBorrado(item) {
  const res = await Swal.fire({
    title: `¿Desactivar "${item.nombre}"?`,
    text: "La máquina será marcada como inactiva.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Sí, desactivar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "var(--d-brown)",
    cancelButtonColor: "var(--d-green)",
  });
  if (res.isConfirmed) {
    try {
      await eliminarCatalogoMaquina(item.id);
      await cargar();
      Swal.fire({
        icon: "success",
        title: "Listo",
        text: "Máquina desactivada",
        timer: 1800,
        showConfirmButton: false,
      });
    } catch {
      Swal.fire("Error", "No se pudo desactivar la máquina", "error");
    }
  }
}

onMounted(() => {
  cargar();
  modalInstance = new Modal(modalElement.value);
});
</script>

<template>
  <div class="crud-container" data-aos="fade-right">
    <CrudToolbar
      title="Catálogo de máquinas"
      v-model="filtro"
      searchPlaceholder="Buscar por nombre o descripción..."
    >
      <template #actions>
        <button class="btn btn-nuevo btn-header" @click="abrirModal(null)">
          <i class="bi bi-plus-circle-fill"></i>
          <span class="btn-header-texto ms-2">Nueva máquina</span>
        </button>
      </template>
    </CrudToolbar>

    <!-- TABLA ESCRITORIO -->
    <div class="divisan-table-container shadow-lg d-none d-lg-block">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-verde"></div>
      </div>
      <table v-else class="table table-divisan align-middle mb-0">
        <thead>
          <tr>
            <th class="ps-4">
              <ColumnFilterMenu
                field="nombre"
                label="Nombre"
                :filterable="false"
                :items="filtrados"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                @update:sort="onSort"
              />
            </th>
            <th>
              <ColumnFilterMenu
                field="descripcion"
                label="Descripción"
                :filterable="false"
                :items="filtrados"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                @update:sort="onSort"
              />
            </th>
            <th class="text-center">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in paginados" :key="item.id">
            <td class="ps-4 fw-bold text-cafe">{{ item.nombre }}</td>
            <td>{{ item.descripcion || "—" }}</td>
            <td class="text-center">
              <div class="d-flex justify-content-center gap-2">
                <button class="btn-circular" @click="abrirModal(item)" title="Editar">
                  <i class="bi bi-pencil-square"></i>
                </button>
                <button
                  class="btn-circular text-danger"
                  @click="confirmarBorrado(item)"
                  title="Desactivar"
                >
                  <i class="bi bi-trash3-fill"></i>
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="paginados.length === 0 && !loading">
            <td colspan="3" class="text-center py-5 text-cafe opacity-50 fw-bold">
              No se encontraron máquinas
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Paginación escritorio -->
      <div class="paginacion-wrapper">
        <div class="pag-izq">
          <small class="pag-info"
            >{{ rangoInicio }}–{{ rangoFin }} de {{ ordenados.length }} máquinas</small
          >
          <div class="por-pagina-wrap">
            <label class="por-pagina-label">Filas:</label>
            <select
              class="por-pagina-select"
              :value="porPagina"
              @change="cambiarPorPagina(Number($event.target.value))"
            >
              <option v-for="n in paginacionStore.opcionesPorPagina" :key="n" :value="n">
                {{ n }}
              </option>
            </select>
          </div>
        </div>
        <div class="pag-der">
          <button
            class="btn-pag"
            :disabled="paginaActual === 1"
            @click="paginaActual = 1"
            title="Primera"
          >
            <i class="bi bi-chevron-double-left"></i>
          </button>
          <button class="btn-pag" :disabled="paginaActual === 1" @click="paginaActual--">
            <i class="bi bi-chevron-left"></i>
          </button>
          <template v-for="p in paginasVisibles" :key="p">
            <span v-if="p === '...'" class="pag-ellipsis">…</span>
            <button
              v-else
              :class="['btn-pag-num', { activo: p === paginaActual }]"
              @click="paginaActual = p"
            >
              {{ p }}
            </button>
          </template>
          <button
            class="btn-pag"
            :disabled="paginaActual === totalPaginas"
            @click="paginaActual++"
          >
            <i class="bi bi-chevron-right"></i>
          </button>
          <button
            class="btn-pag"
            :disabled="paginaActual === totalPaginas"
            @click="paginaActual = totalPaginas"
            title="Última"
          >
            <i class="bi bi-chevron-double-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- CARDS MÓVIL -->
    <div class="d-lg-none">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-verde"></div>
      </div>
      <div
        v-else
        v-for="item in paginados"
        :key="'mob-' + item.id"
        class="divisan-mobile-card mb-3 p-3 shadow-sm"
      >
        <div class="fw-bold text-cafe card-name mb-1">{{ item.nombre }}</div>
        <div class="small text-muted-cafe mb-3">
          {{ item.descripcion || "Sin descripción" }}
        </div>
        <div class="d-flex justify-content-end gap-3 pt-2 border-top">
          <i
            class="bi bi-pencil-square text-verde fs-5"
            style="cursor: pointer"
            @click="abrirModal(item)"
          ></i>
          <i
            class="bi bi-trash3-fill text-danger fs-5"
            style="cursor: pointer"
            @click="confirmarBorrado(item)"
          ></i>
        </div>
      </div>
      <!-- Paginación móvil -->
      <div class="paginacion-mobile">
        <div class="pag-mobile-top">
          <small class="pag-info"
            >{{ rangoInicio }}–{{ rangoFin }} de {{ ordenados.length }} máquinas</small
          >
          <div class="por-pagina-wrap">
            <label class="por-pagina-label">Filas:</label>
            <select
              class="por-pagina-select"
              :value="porPagina"
              @change="cambiarPorPagina(Number($event.target.value))"
            >
              <option v-for="n in paginacionStore.opcionesPorPagina" :key="n" :value="n">
                {{ n }}
              </option>
            </select>
          </div>
        </div>
        <div class="pag-mobile-nav">
          <button
            class="btn-pag-mobile"
            :disabled="paginaActual === 1"
            @click="paginaActual--"
          >
            <i class="bi bi-chevron-left"></i> Anterior
          </button>
          <small class="text-verde fw-bold"
            >{{ paginaActual }} / {{ totalPaginas }}</small
          >
          <button
            class="btn-pag-mobile"
            :disabled="paginaActual === totalPaginas"
            @click="paginaActual++"
          >
            Siguiente <i class="bi bi-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- MODAL -->
    <div class="modal fade" tabindex="-1" aria-hidden="true" ref="modalElement">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content divisan-modal border-0">
          <div class="modal-header border-0 pb-0">
            <h5 class="modal-title fw-bold text-cafe">
              {{ editando ? "Editar máquina" : "Nueva máquina" }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <form @submit.prevent="guardar">
            <div class="modal-body">
              <div class="mb-3">
                <label class="form-label label-divisan"
                  >Nombre <span class="text-danger">*</span></label
                >
                <input
                  v-model="form.nombre"
                  type="text"
                  class="form-control divisan-input-modal"
                  required
                  placeholder="Ej. Inyectora 01"
                />
              </div>
              <div class="mb-3">
                <label class="form-label label-divisan"
                  >Descripción <span class="label-opcional">(opcional)</span></label
                >
                <textarea
                  v-model="form.descripcion"
                  class="form-control divisan-input-modal"
                  rows="3"
                  placeholder="Descripción de la máquina..."
                ></textarea>
              </div>
            </div>
            <div class="modal-footer border-0 pt-0">
              <button
                type="submit"
                class="btn btn-nuevo w-100 py-2"
                :disabled="guardando"
              >
                <span
                  v-if="guardando"
                  class="spinner-border spinner-border-sm me-2"
                ></span>
                {{ editando ? "Actualizar máquina" : "Guardar máquina" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.text-cafe {
  color: var(--d-brown);
}
.text-verde {
  color: var(--d-green);
}
.text-muted-cafe {
  color: var(--d-brown);
  opacity: 0.6;
  font-size: 0.9rem;
}

.btn-nuevo {
  background-color: var(--d-brown);
  color: var(--d-cream);
  border: none;
  font-weight: 600;
}
.btn-nuevo:hover:not(:disabled) {
  background-color: #703808;
  color: var(--d-cream);
}
.btn-header {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

.divisan-card {
  background-color: var(--d-cream);
  border-radius: 12px;
  border-left: 5px solid var(--d-green);
}
.divisan-input {
  background-color: white !important;
  border: 1px solid var(--d-green);
  color: var(--d-brown) !important;
  border-radius: 8px;
}
.search-wrapper {
  position: relative;
}
.search-wrapper i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--d-green);
  z-index: 5;
  pointer-events: none;
}
.search-wrapper input {
  padding-left: 35px;
}

.divisan-table-container {
  background-color: var(--d-cream);
  border-radius: var(--d-radius-lg);
  box-shadow: var(--d-elevation-2);
  overflow: hidden;
}
.table-divisan thead th {
  background: var(--d-cream);
  color: var(--d-brown);
  border-bottom: 1px solid var(--d-border);
}
.table-divisan th {
  padding: 15px;
  font-weight: 800;
  text-transform: capitalize;
  border: none;
  white-space: nowrap;
}
.table-divisan td {
  border-bottom: 1px solid rgba(96, 108, 56, 0.1);
  padding: 12px 15px;
}
.table-divisan tbody tr:hover td {
  background-color: rgba(96, 108, 56, 0.05);
}

.btn-circular {
  background: white;
  border: 1px solid var(--d-green);
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--d-green);
  transition: 0.2s;
}
.btn-circular:hover {
  background-color: var(--d-green);
  color: var(--d-cream);
}

.divisan-modal {
  background-color: var(--d-cream);
  border: 2px solid var(--d-brown);
  border-radius: 20px;
}
.label-divisan {
  color: var(--d-brown);
  font-weight: 700;
}
.label-opcional {
  font-size: 0.72rem;
  font-weight: 400;
  color: var(--d-brown);
  opacity: 0.5;
}
.divisan-input-modal {
  border: 1px solid var(--d-green);
  border-radius: 8px;
}

.divisan-mobile-card {
  background-color: var(--d-cream);
  border-radius: 15px;
  border-left: 6px solid var(--d-green);
}

.paginacion-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  flex-wrap: wrap;
  gap: 8px;
  border-top: 1px solid rgba(64, 34, 8, 0.1);
  background: rgba(96, 108, 56, 0.05);
}
.pag-izq {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}
.pag-der {
  display: flex;
  align-items: center;
  gap: 4px;
}
.pag-info {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--d-green);
  white-space: nowrap;
}
.por-pagina-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}
.por-pagina-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--d-green);
  white-space: nowrap;
}
.por-pagina-select {
  appearance: none;
  -webkit-appearance: none;
  background: white
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%23606c38' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E")
    no-repeat right 8px center;
  border: 1.5px solid var(--d-green);
  border-radius: 8px;
  padding: 3px 26px 3px 10px;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--d-brown);
  cursor: pointer;
  outline: none;
  transition: border-color 0.15s;
  min-width: 58px;
}
.por-pagina-select:focus,
.por-pagina-select:hover {
  border-color: var(--d-brown);
}
.btn-pag {
  background: white;
  border: 1.5px solid rgba(96, 108, 56, 0.35);
  color: var(--d-brown);
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s;
  font-size: 0.8rem;
}
.btn-pag:hover:not(:disabled) {
  background: var(--d-green);
  color: var(--d-cream);
  border-color: var(--d-green);
}
.btn-pag:disabled {
  opacity: 0.3;
  cursor: default;
}
.btn-pag-num {
  background: white;
  border: 1.5px solid rgba(96, 108, 56, 0.25);
  color: var(--d-brown);
  border-radius: 8px;
  min-width: 32px;
  height: 32px;
  padding: 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s;
  font-size: 0.82rem;
  font-weight: 700;
}
.btn-pag-num:hover {
  background: rgba(96, 108, 56, 0.1);
  border-color: var(--d-green);
}
.btn-pag-num.activo {
  background: var(--d-brown);
  color: var(--d-cream);
  border-color: var(--d-brown);
  pointer-events: none;
}
.pag-ellipsis {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 32px;
  font-size: 0.85rem;
  color: var(--d-brown);
  opacity: 0.4;
  font-weight: 700;
}

.paginacion-mobile {
  border-top: 1px solid rgba(64, 34, 8, 0.15);
  padding: 10px 4px;
  margin-top: 4px;
}
.pag-mobile-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}
.pag-mobile-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.btn-pag-mobile {
  background: var(--d-cream);
  border: 1px solid var(--d-green);
  color: var(--d-brown);
  border-radius: 10px;
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 110px;
  justify-content: center;
}
.btn-pag-mobile:hover:not(:disabled) {
  background-color: var(--d-green);
  color: var(--d-cream);
}
.btn-pag-mobile:disabled {
  opacity: 0.35;
  cursor: default;
}

.card-name {
  font-size: 0.92rem;
}

@media (max-width: 991.98px) {
  .btn-header-texto {
    display: none;
  }
  .btn-header {
    width: 36px;
    height: 36px;
    padding: 0;
    justify-content: center;
    border-radius: 50%;
  }
}

@media (max-width: 768px) {
  .paginacion-wrapper {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
}
</style>
